// "Dia de trabalho" da loja: vira na hora configurada (padrão 3h), não à meia-noite.
// Assim a entrega das 23h50 continua contando no dia em que saiu.

import type { RegrasDaLoja } from './periodo';

const UMA_HORA_MS = 3_600_000;

/** Instante salvo pelo banco (ISO) ou já convertido; vazio nunca cai no dia. */
type Instante = string | Date | null | undefined;

/**
 * Para filtrar uma lista pelo "hoje" da loja (Concluídas, contagem da aba): a janela do dia é
 * calculada uma vez e cada item só compara números.
 */
export function filtroDoDiaOperacional(
  loja: RegrasDaLoja,
  agora: Date = new Date(),
): (instante: Instante) => boolean {
  const janela = janelaDoDiaOperacional(agora, loja.viradaHora, loja.fuso);
  const inicio = Date.parse(janela.inicio);
  const fim = Date.parse(janela.fim);
  return (instante) => {
    if (instante === null || instante === undefined) return false;
    const t = typeof instante === 'string' ? Date.parse(instante) : instante.getTime();
    return Number.isFinite(t) && t >= inicio && t < fim;
  };
}

/** Instante (UTC) em que começou o dia operacional que contém `agora`. */
export function inicioDoDiaOperacional(agora: Date, viradaHora: number, fuso: string): Date {
  const local = partesNoFuso(agora, fuso);
  // Antes da virada ainda é o dia anterior. Date.UTC normaliza o dia 0 para o fim do mês anterior.
  const dia = local.hora < viradaHora ? local.dia - 1 : local.dia;
  return deHoraLocalParaUtc(local.ano, local.mes, dia, viradaHora, fuso);
}

/** Janela [início, fim) do dia operacional, em ISO, pronta para filtrar no banco. */
export function janelaDoDiaOperacional(
  agora: Date,
  viradaHora: number,
  fuso: string,
): { inicio: string; fim: string } {
  const inicio = inicioDoDiaOperacional(agora, viradaHora, fuso);
  const fim = inicioDoDiaOperacional(
    new Date(inicio.getTime() + 26 * UMA_HORA_MS),
    viradaHora,
    fuso,
  );
  return { inicio: inicio.toISOString(), fim: fim.toISOString() };
}

/** Data local (ano, mês 1-12, dia) do dia operacional que contém `instante`. */
export function dataDoDiaOperacional(
  instante: Date,
  viradaHora: number,
  fuso: string,
): { ano: number; mes: number; dia: number } {
  const local = partesNoFuso(instante, fuso);
  // Date.UTC normaliza o dia 0 para o fim do mês anterior.
  const d = new Date(
    Date.UTC(local.ano, local.mes - 1, local.hora < viradaHora ? local.dia - 1 : local.dia),
  );
  return { ano: d.getUTCFullYear(), mes: d.getUTCMonth() + 1, dia: d.getUTCDate() };
}

/** "2026-10-21": o dia operacional que contém `instante`, no formato em que o banco guarda (date). */
export function diaOperacionalIso(instante: Date, viradaHora: number, fuso: string): string {
  const d = dataDoDiaOperacional(instante, viradaHora, fuso);
  return new Date(Date.UTC(d.ano, d.mes - 1, d.dia)).toISOString().slice(0, 10);
}

/** Soma (ou subtrai) dias de uma data "AAAA-MM-DD", atravessando mês e ano. */
export function somarDias(dia: string, dias: number): string {
  const [ano, mes, d] = dia.split('-').map(Number);
  return new Date(Date.UTC(ano ?? 1970, (mes ?? 1) - 1, (d ?? 1) + dias))
    .toISOString()
    .slice(0, 10);
}

/** Instante (UTC) da virada do dia local informado. Aceita dia/mês fora da faixa (normaliza). */
export function inicioDoDiaLocal(
  ano: number,
  mes: number,
  dia: number,
  viradaHora: number,
  fuso: string,
): Date {
  return deHoraLocalParaUtc(ano, mes, dia, viradaHora, fuso);
}

/** Hora local (0-23) de um instante no fuso da loja. */
export function horaLocal(instante: Date, fuso: string): number {
  return partesNoFuso(instante, fuso).hora;
}

interface PartesLocais {
  ano: number;
  mes: number;
  dia: number;
  hora: number;
}

const formatadores = new Map<string, Intl.DateTimeFormat>();

function partesNoFuso(instante: Date, fuso: string): PartesLocais {
  let fmt = formatadores.get(fuso);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: fuso,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
    });
    formatadores.set(fuso, fmt);
  }
  const valor = (tipo: Intl.DateTimeFormatPartTypes) =>
    Number(fmt.formatToParts(instante).find((p) => p.type === tipo)?.value);
  return { ano: valor('year'), mes: valor('month'), dia: valor('day'), hora: valor('hour') };
}

/** Diferença (ms) entre o relógio do fuso e o UTC naquele instante. */
function deslocamento(instante: Date, fuso: string): number {
  const p = partesNoFuso(instante, fuso);
  const comoUtc = Date.UTC(p.ano, p.mes - 1, p.dia, p.hora);
  const truncado = Date.UTC(
    instante.getUTCFullYear(),
    instante.getUTCMonth(),
    instante.getUTCDate(),
    instante.getUTCHours(),
  );
  return comoUtc - truncado;
}

function deHoraLocalParaUtc(
  ano: number,
  mes: number,
  dia: number,
  hora: number,
  fuso: string,
): Date {
  const palpite = Date.UTC(ano, mes - 1, dia, hora);
  const primeiro = palpite - deslocamento(new Date(palpite), fuso);
  // Segunda passada corrige a borda de horário de verão, se o fuso tiver.
  return new Date(palpite - deslocamento(new Date(primeiro), fuso));
}
